import type { Coordinate, MultiPolygonCoordinates } from '../models/editor-feature';

/**
 * How many points a shape is drawn with.
 *
 * A border traced from CAD arrives with hundreds of vertices — every arc flattened, every
 * doorway stepped — and the moment the Edit points tool is armed on one, the screen fills
 * with grips too close together to grab. The outline is not wrong; it is simply drawn at a
 * detail nobody editing a floorplan needs.
 *
 * So the detail becomes a control. Points are dropped in order of how little they contribute
 * to the shape, which is Visvalingam–Whyatt: the vertex forming the smallest triangle with
 * its two neighbours is the one whose removal changes the outline least. Ranking rather than
 * a tolerance, because a slider has to answer "give me forty points", not "give me whatever
 * 0.03 units of error produces".
 *
 * Nothing here mutates: a caller holds the shape it started from and asks this for a version
 * of it, so sliding down and back up is lossless. Dropping vertices in place and trying to
 * put them back would not be — a midpoint is not the corner that used to be there.
 */

/** The fewest points a closed ring can have and still enclose anything: a triangle. */
export const MINIMUM_RING_POINTS = 4;

/** The area of the triangle a vertex makes with its neighbours. */
const triangleArea = (before: Coordinate, at: Coordinate, after: Coordinate): number => Math.abs(
  (before[0] - at[0]) * (after[1] - at[1]) - (after[0] - at[0]) * (before[1] - at[1]),
) / 2;

const isClosed = (ring: readonly Coordinate[]): boolean => (
  ring.length > 1
  && ring[0][0] === ring[ring.length - 1][0]
  && ring[0][1] === ring[ring.length - 1][1]
);

/**
 * One ring, thinned to a number of points by dropping the least significant first.
 *
 * The closing repeat is preserved: an open ring is invalid GeoJSON, renders as nothing and
 * saves without complaint, which is the worst of the three possible failures.
 */
export const thinRing = (ring: readonly Coordinate[], target: number): Coordinate[] => {
  const closed = isClosed(ring);
  const points = closed ? ring.slice(0, -1) : [...ring];
  // The closing repeat is not a point anybody is choosing to keep, so the target counts the
  // distinct ones.
  const keep = Math.max(3, closed ? target - 1 : target);

  if (points.length <= keep) {
    return [...ring];
  }

  const alive = points.map(() => true);
  let remaining = points.length;

  const neighboursOf = (index: number): [number, number] => {
    let before = index;
    let after = index;

    do { before = (before - 1 + points.length) % points.length; } while (!alive[before]);
    do { after = (after + 1) % points.length; } while (!alive[after]);

    return [before, after];
  };

  while (remaining > keep) {
    let weakest = -1;
    let weakestArea = Infinity;

    for (let index = 0; index < points.length; index += 1) {
      if (alive[index]) {
        const [before, after] = neighboursOf(index);
        const area = triangleArea(points[before], points[index], points[after]);

        if (area < weakestArea) {
          weakestArea = area;
          weakest = index;
        }
      }
    }

    if (weakest < 0) {
      break;
    }

    alive[weakest] = false;
    remaining -= 1;
  }

  const thinned = points.filter((_, index) => alive[index]);

  return closed ? [...thinned, thinned[0]] : thinned;
};

/** Every point in a shape, across all its polygons and rings. */
export const pointCountOf = (coordinates: MultiPolygonCoordinates): number => coordinates.reduce(
  (total, polygon) => total + polygon.reduce((subtotal, ring) => subtotal + ring.length, 0),
  0,
);

/**
 * A whole shape at a given number of points.
 *
 * The budget is split between rings in proportion to what each started with, so thinning a
 * room with a courtyard in it takes detail off both rather than flattening the hole into a
 * triangle while the outer wall keeps every vertex. Each ring keeps at least a triangle,
 * which is why the result can exceed a very small target — a shape cannot have fewer points
 * than its rings need to exist.
 */
export const withPointCount = (
  coordinates: MultiPolygonCoordinates,
  target: number,
): MultiPolygonCoordinates => {
  const total = pointCountOf(coordinates);

  if (total === 0 || target >= total) {
    return coordinates;
  }

  const ratio = Math.max(target, MINIMUM_RING_POINTS) / total;

  return coordinates.map(
    (polygon) => polygon.map(
      (ring) => thinRing(ring, Math.max(MINIMUM_RING_POINTS, Math.round(ring.length * ratio))),
    ),
  );
};
